"use client";

import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import { useId } from "react";
import { Tooltip } from "react-tooltip";
import type { UserPageBrainActivityCell, UserPageBrainActivityViewModel } from "./userPageBrainActivity.helpers";
import { CELL_GAP_PX, CELL_FRAME_STYLE, CELL_SIZE_PX, DAY_LABEL_COLUMN_WIDTH_PX, DAY_LABEL_TOP_OFFSET_PX, DAY_LABELS, HEATMAP_CONTENT_CLASS_NAME, HEATMAP_GRID_STYLE, LOADING_HEATMAP_WEEK_COUNT, HEATMAP_VIEWPORT_CLASS_NAME, LOADING_MONTH_HEADER_SEGMENTS, MONTH_LABEL_ROW_HEIGHT_PX, TOOLTIP_DATE_FORMATTER, TOOLTIP_STYLE, type HeatmapTooltipData, getHeatmapColumnLeftPx, getHeatmapGridWidthPx, getCellFrameClassName, getCellNodeClassName, getCellTooltipAnchorProps, getHeatmapTooltipData, getPlacedMonthLabels, getTooltipCountLabel } from "./userPageBrainActivityHeatmap.helpers";
import { useHeatmapViewport } from "./userPageBrainActivityHeatmap.viewport";
const LOADING_GRID_WIDTH_PX = getHeatmapGridWidthPx(LOADING_HEATMAP_WEEK_COUNT);
const LOADING_HEATMAP_CELLS = Array.from({
  length: LOADING_HEATMAP_WEEK_COUNT * 7
}, (_, index) => {
  const column = Math.floor(index / 7);
  const row = index % 7;
  return {
    key: `loading-${column}-${row}`,
    isPadding: column === 0 && row < 2 || column === LOADING_HEATMAP_WEEK_COUNT - 1 && row > 4,
    isAccent: (column + row) % 5 === 0
  };
});
export function UserPageBrainActivityHeatmapLoading() {
  const $ = _c(5);
  const {
    viewportRef
  } = useHeatmapViewport("loading");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HeatmapDayLabels />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeatmapLoadingMonthLabels />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={HEATMAP_CONTENT_CLASS_NAME}>{t1}<div className="tw-grid tw-animate-pulse tw-grid-flow-col tw-grid-rows-7" aria-hidden="true" style={HEATMAP_GRID_STYLE}>{LOADING_HEATMAP_CELLS.map(_temp)}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== viewportRef) {
    t3 = <div className="tw-flex tw-gap-3 tw-px-4" aria-label="Loading activity heatmap">{t0}<div ref={viewportRef} className={HEATMAP_VIEWPORT_CLASS_NAME}>{t2}</div></div>;
    $[3] = viewportRef;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp(cell) {
  return <HeatmapLoadingCell key={cell.key} isPadding={cell.isPadding} isAccent={cell.isAccent} />;
}
export function UserPageBrainActivityHeatmap(t0) {
  const $ = _c(20);
  const {
    activity
  } = t0;
  const tooltipId = useId();
  const {
    viewportRef
  } = useHeatmapViewport(activity.resetKey);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeatmapDayLabels />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== activity) {
    t2 = <HeatmapMonthLabels activity={activity} />;
    $[1] = activity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `Public activity heatmap for ${activity.periodLabel}`;
  let t4;
  if ($[3] !== activity.cells || $[4] !== tooltipId) {
    let t5;
    if ($[6] !== tooltipId) {
      t5 = cell => <HeatmapCell key={cell.key} cell={cell} tooltipId={tooltipId} />;
      $[6] = tooltipId;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = activity.cells.map(t5);
    $[3] = activity.cells;
    $[4] = tooltipId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="tw-grid tw-grid-flow-col tw-grid-rows-7" role="img" aria-label={t3} style={HEATMAP_GRID_STYLE}>{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== tooltipId) {
    t6 = <Tooltip id={tooltipId} place="top" offset={8} opacity={1} style={TOOLTIP_STYLE} render={_temp2} />;
    $[11] = tooltipId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={HEATMAP_CONTENT_CLASS_NAME}>{t2}{t5}{t6}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7 || $[18] !== viewportRef) {
    t8 = <div className="tw-flex tw-gap-3 tw-px-4">{t1}<div ref={viewportRef} className={HEATMAP_VIEWPORT_CLASS_NAME}>{t7}</div></div>;
    $[17] = t7;
    $[18] = viewportRef;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp2(t0) {
  const {
    activeAnchor
  } = t0;
  const tooltipData = getHeatmapTooltipData(activeAnchor);
  return tooltipData ? <HeatmapTooltipContent {...tooltipData} /> : null;
}
function HeatmapDayLabels() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-hidden tw-shrink-0 tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-widest tw-text-iron-600 sm:tw-grid" aria-hidden="true" style={{
      marginTop: `${DAY_LABEL_TOP_OFFSET_PX}px`,
      width: `${DAY_LABEL_COLUMN_WIDTH_PX}px`,
      gridTemplateRows: `repeat(7, ${CELL_SIZE_PX}px)`,
      rowGap: `${CELL_GAP_PX}px`
    }}>{DAY_LABELS.map(_temp3)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp3(label, index) {
  return <div key={`${label || "empty"}-${index}`} className="tw-flex tw-items-center tw-justify-end">{label || <span className="tw-opacity-0">Sun</span>}</div>;
}
function HeatmapLoadingCell(t0) {
  const $ = _c(5);
  const {
    isPadding,
    isAccent
  } = t0;
  let t1;
  if ($[0] !== isAccent || $[1] !== isPadding) {
    t1 = !isPadding && <div className={clsx("tw-rounded-[2px]", isAccent ? "tw-h-[9px] tw-w-[9px] tw-bg-white/[0.09]" : "tw-h-[7px] tw-w-[7px] tw-bg-white/[0.05]")} />;
    $[0] = isAccent;
    $[1] = isPadding;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="tw-flex tw-items-center tw-justify-center" style={CELL_FRAME_STYLE}>{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function HeatmapCell(t0) {
  const $ = _c(11);
  const {
    cell,
    tooltipId
  } = t0;
  let t1;
  if ($[0] !== cell || $[1] !== tooltipId) {
    t1 = getCellTooltipAnchorProps(cell, tooltipId);
    $[0] = cell;
    $[1] = tooltipId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== cell) {
    t2 = clsx("tw-group/cell tw-relative", getCellFrameClassName(cell));
    $[3] = cell;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== cell) {
    t3 = cell.state !== "padding" && <div className={getCellNodeClassName(cell, "grid")} />;
    $[5] = cell;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div {...t1} className={t2} style={CELL_FRAME_STYLE}>{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function HeatmapTooltipContent(t0) {
  const $ = _c(26);
  const {
    count,
    isoDate,
    intensity
  } = t0;
  const t1 = count > 0 ? "active" : "empty";
  let t2;
  let t3;
  let t4;
  if ($[0] !== count || $[1] !== intensity || $[2] !== isoDate || $[3] !== t1) {
    const indicatorCell = {
      key: "tooltip",
      isoDate,
      count,
      ariaLabel: null,
      state: t1,
      intensity
    };
    t4 = "tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-[#1c2128]/95 tw-px-3.5 tw-py-2 tw-shadow-[0_10px_30px_rgba(0,0,0,0.8)] tw-backdrop-blur-md";
    t3 = "tw-flex tw-h-3 tw-w-3 tw-items-center tw-justify-center";
    t2 = getCellNodeClassName(indicatorCell, "tooltip");
    $[0] = count;
    $[1] = intensity;
    $[2] = isoDate;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2) {
    t5 = <span className={t2} />;
    $[7] = t2;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <span className={t3}>{t5}</span>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== count) {
    t7 = getTooltipCountLabel(count);
    $[12] = count;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="tw-text-xs tw-font-semibold tw-text-iron-50">{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="tw-h-3 tw-w-px tw-bg-white/15" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isoDate) {
    t10 = TOOLTIP_DATE_FORMATTER.format(new Date(`${isoDate}T00:00:00.000Z`));
    $[17] = isoDate;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <span className="tw-text-xs tw-font-medium tw-text-iron-400">{t10}</span>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t4 || $[23] !== t6 || $[24] !== t8) {
    t12 = <div className={t4}>{t6}{t8}{t9}{t11}</div>;
    $[21] = t11;
    $[22] = t4;
    $[23] = t6;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function HeatmapLoadingMonthLabels() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-relative tw-mb-1 tw-overflow-hidden" aria-hidden="true" style={{
      height: `${MONTH_LABEL_ROW_HEIGHT_PX}px`,
      width: `${LOADING_GRID_WIDTH_PX}px`
    }}>{LOADING_MONTH_HEADER_SEGMENTS.map(_temp4)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp4(segment) {
  return <span key={segment.key} className="tw-absolute tw-bottom-0 tw-h-[5px] tw-animate-pulse tw-rounded-full tw-bg-white/[0.08]" style={{
    left: `${getHeatmapColumnLeftPx(segment.labelColumn)}px`,
    width: `${segment.widthPx}px`
  }} />;
}
function HeatmapMonthLabels(t0) {
  const $ = _c(7);
  const {
    activity
  } = t0;
  let t1;
  if ($[0] !== activity.monthLabels || $[1] !== activity.weekCount) {
    const placedMonthLabels = getPlacedMonthLabels(activity.monthLabels);
    let t2;
    if ($[3] !== activity.weekCount) {
      t2 = getHeatmapGridWidthPx(activity.weekCount);
      $[3] = activity.weekCount;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const gridWidthPx = t2;
    const t3 = `${gridWidthPx}px`;
    let t4;
    if ($[5] !== t3) {
      t4 = {
        height: `${MONTH_LABEL_ROW_HEIGHT_PX}px`,
        width: t3
      };
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t1 = <div className="tw-relative tw-mb-1 tw-overflow-hidden tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-widest tw-text-iron-600" aria-hidden="true" style={t4}>{placedMonthLabels.map(_temp5)}</div>;
    $[0] = activity.monthLabels;
    $[1] = activity.weekCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp5(label) {
  return <span key={label.key} className="tw-absolute tw-bottom-0 tw-whitespace-nowrap tw-leading-none" style={{
    left: `${label.leftPx}px`
  }}>{label.label}</span>;
}